import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { galleryPhotos } from '../models/galleryPhotos';
const SLIDE_DURATION_MS = 5000;
const PhotoGalleryWidget = () => {
    const visiblePhotos = useMemo(() => galleryPhotos.filter((photo) => photo.src), []);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const goToPrevious = useCallback(() => {
        if (visiblePhotos.length === 0) {
            return;
        }
        setActiveIndex((currentIndex) => (currentIndex - 1 + visiblePhotos.length) % visiblePhotos.length);
    }, [visiblePhotos.length]);
    const goToNext = useCallback(() => {
        if (visiblePhotos.length === 0) {
            return;
        }
        setActiveIndex((currentIndex) => (currentIndex + 1) % visiblePhotos.length);
    }, [visiblePhotos.length]);
    const openLightbox = useCallback(() => setIsLightboxOpen(true), []);
    const closeLightbox = useCallback(() => setIsLightboxOpen(false), []);
    useEffect(() => {
        if (visiblePhotos.length < 2) {
            return undefined;
        }
        if (isPaused || isLightboxOpen) {
            return undefined;
        }
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
            return undefined;
        }
        const intervalId = window.setInterval(() => {
            setActiveIndex((currentIndex) => (currentIndex + 1) % visiblePhotos.length);
        }, SLIDE_DURATION_MS);
        return () => window.clearInterval(intervalId);
    }, [isPaused, isLightboxOpen, visiblePhotos.length]);
    useEffect(() => {
        if (!isLightboxOpen) {
            return undefined;
        }
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeLightbox();
            }
            if (event.key === 'ArrowLeft') {
                goToPrevious();
            }
            if (event.key === 'ArrowRight') {
                goToNext();
            }
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isLightboxOpen, closeLightbox, goToPrevious, goToNext]);
    if (visiblePhotos.length === 0) {
        return (_jsx("section", { className: "photo-gallery-widget", children: _jsx("div", { className: "photo-gallery-empty", children: "Photos coming soon" }) }));
    }
    const activePhoto = visiblePhotos[activeIndex];
    return (_jsxs("section", { className: "photo-gallery-widget", children: [_jsxs("div", { className: "photo-gallery-slideshow", onMouseEnter: () => setIsPaused(true), onMouseLeave: () => setIsPaused(false), onFocus: () => setIsPaused(true), onBlur: () => setIsPaused(false), children: [_jsxs("div", { className: "photo-gallery-stage", children: [visiblePhotos.map((photo, index) => (_jsx("button", { type: "button", className: index === activeIndex
                                    ? 'photo-gallery-slide is-active'
                                    : 'photo-gallery-slide', onClick: openLightbox, "aria-hidden": index !== activeIndex, "aria-label": `Open ${photo.alt} full screen`, tabIndex: index === activeIndex ? 0 : -1, children: _jsx("img", { src: photo.src, alt: photo.alt, loading: index === 0 ? 'eager' : 'lazy' }) }, photo.id))), visiblePhotos.length > 1 && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "photo-gallery-slide-nav photo-gallery-slide-prev", onClick: goToPrevious, "aria-label": "Previous photo", children: "\u2039" }), _jsx("button", { type: "button", className: "photo-gallery-slide-nav photo-gallery-slide-next", onClick: goToNext, "aria-label": "Next photo", children: "\u203A" })] }))] }), visiblePhotos.length > 1 && (_jsx("div", { className: "photo-gallery-dots", role: "tablist", "aria-label": "Choose slide", children: visiblePhotos.map((photo, index) => (_jsx("button", { type: "button", role: "tab", "aria-selected": index === activeIndex, "aria-label": `Go to slide ${index + 1}`, className: index === activeIndex
                                ? 'photo-gallery-dot is-active'
                                : 'photo-gallery-dot', onClick: () => setActiveIndex(index) }, photo.id))) }))] }), isLightboxOpen && (_jsxs("div", { className: "photo-gallery-lightbox", role: "dialog", "aria-label": "Photo viewer", onClick: closeLightbox, children: [_jsx("button", { type: "button", className: "photo-gallery-lightbox-close", onClick: closeLightbox, "aria-label": "Close photo viewer", children: "\u00D7" }), visiblePhotos.length > 1 && (_jsx("button", { type: "button", className: "photo-gallery-lightbox-nav photo-gallery-lightbox-prev", onClick: (event) => {
                            event.stopPropagation();
                            goToPrevious();
                        }, "aria-label": "Previous photo", children: "\u2039" })), _jsx("img", { className: "photo-gallery-lightbox-image", src: activePhoto.src, alt: activePhoto.alt, onClick: (event) => event.stopPropagation() }), visiblePhotos.length > 1 && (_jsx("button", { type: "button", className: "photo-gallery-lightbox-nav photo-gallery-lightbox-next", onClick: (event) => {
                            event.stopPropagation();
                            goToNext();
                        }, "aria-label": "Next photo", children: "\u203A" }))] }))] }));
};
export default PhotoGalleryWidget;
